import { common } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import createPalette from '@mui/material/styles/createPalette';

// SETUP COLORS
const GREY = {
	0: '#FFFFFF',
	100: '#F9FAFB',
	200: '#F4F6F8',
	300: '#DFE3E8',
	400: '#C4CDD5',
	500: '#919EAB',
	600: '#637381',
	700: '#454F5B',
	800: '#212B36',
	900: '#161C24',
	500_8: alpha('#919EAB', 0.08),
	500_12: alpha('#919EAB', 0.12),
	500_16: alpha('#919EAB', 0.16),
	500_24: alpha('#919EAB', 0.24),
	500_32: alpha('#919EAB', 0.32),
	500_48: alpha('#919EAB', 0.48),
	500_56: alpha('#919EAB', 0.56),
	500_80: alpha('#919EAB', 0.8),
};

// The Product Brand
const ROOT = {
	basicWhite: '#fff',
	basicWhite_006: alpha('#ffffff', 0.6),
	basicWhite_007: alpha('#ffffff', 0.1),
	basicDark: '#404040',
	basicDark_60: alpha('#404040', 0.6),
	basicDark_50: alpha('#404040', 0.5),
	basicDeeoGreen: '#004751', //dark green for text
	basicDeeoGreen_02: alpha('#004751', 0.04),
	basicGreen: '#00523e',
	textGreen: '#1C6E5B',
	extraGreen: '#195962',
	titleGreen: '#018f6b',
	brandPrimary: '#2fe5cf', // main brand color - green hospital
	brandPrimary_008: 'rgba(0, 138, 105, 0.08)',
	brandSecondary: '#008a69',
	brandSecondary_012: alpha('#008a69', 0.12),
	brandSecondary_013: alpha('#008a69', 0.32),
	brandSecondary_014: alpha('#008a69', 0.08),
	brandSecondary_015: '#F5FBF9',
	brandSecondary1: '#fafdfc', //very light green - e.g. navbar
	brandSecondary2: 'rgba(0, 138, 105, 0.02)', // light green - e.g. sidebar
	brandSecondary3: '#316b73', // draker green - e.g. numbers in step
	basicDeepBlue: '#2c3e50',
	brandTertiary: '#a7a9ac',
	basicDeepGreen: '#00363d',
	basic2: '#d9d9d9',
	basic3: '#8c8c8c',
	basic4: '#666',
	basic5: '#404040',
	basic6: '#e7edee',
	ux01eee: '#eee',
	ux04bbb: '#bbb',
	ux02ddd: '#ddd',
	ux03ccc: '#ccc',
	ux07333: '#333',
	ux07777: '#5d777b',
	grayed: '#f1f6f6',
	darkGrayed: '#cbdcde',
	darkGrayed_028: alpha('#cbdcde', 0.28),
	darkGrayed_024: alpha('#cbdcde', 0.24),
	darkerGrayed: '#e6efef',
	orange: '#f75d12',
	lightOrange: '#e3672a',
	divider: '#d0dfe1',
	graybg: '#f6f8fc',
	errorBackground: alpha('#FF0000', 0.04),
	brown_01: 'rgba(247,93,18,0.24)',
	darkBlue: '#16161D',
	border_teal: '#95AFB1',
	orange_2: alpha('#F75D12', 0.08),
	line: '#CCF8F2',
  favoritePlan:'#6CD7CA'
};

const PRIMARY = {
	lighter: '#D1E9FC',
	light: '#76B0F1',
	main: '#004751',
	dark: '#103996',
	darker: '#061B64',
	contrastText: '#fff',
};

const SECONDARY = {
	lighter: '#D6E4FF',
	light: '#84A9FF',
	main: '#3366FF',
	dark: '#1939B7',
	darker: '#091A7A',
	contrastText: '#fff',
};

const INFO = {
	lighter: '#D0F2FF',
	light: '#D0F2FF',
	main: '#1890FF',
	dark: '#0C53B7',
	darker: '#04297A',
	contrastText: '#fff',
};

const SUCCESS = {
	lighter: '#E9FCD4',
	light: '#AAF27F',
	main: '#54D62C',
	dark: '#229A16',
	darker: '#08660D',
	contrastText: GREY[800],
};

const WARNING = {
	lighter: '#FFF7CD',
	light: '#FFF7CD',
	main: '#FFC107',
	dark: '#B78103',
	darker: '#7A4F01',
	contrastText: GREY[800],
};

const ERROR = {
	lighter: '#FFE7D9',
	light: '#fff0f4',
	main: '#FF4842',
	dark: '#B72136',
	darker: '#7A0C2E',
	contrastText: '#fff',
};

declare module '@mui/material/styles/createPalette' {
	interface CustomPalette {
		root: { [key: string]: string };
		nav: {
			primary: string;
			secondary: string;
		};
	}

	interface Palette extends CustomPalette {}
	interface PaletteOptions extends CustomPalette {}
}

const palette = createPalette({
	root: ROOT,
	nav: { primary: ROOT.brandSecondary1, secondary: ROOT.brandSecondary2 },
	common: { black: '#000', white: '#bae7f1' },
	primary: { ...PRIMARY },
	secondary: { ...SECONDARY },
	info: { ...INFO },
	success: { ...SUCCESS },
	warning: { ...WARNING },
	error: { ...ERROR },
	divider: GREY[500_24],
	text: { primary: '#004751', secondary: GREY[600], disabled: GREY[500] },
	background: { paper: '#fff', default: common.white },
	action: {
		active: GREY[600],
		hover: GREY[500_8],
		selected: GREY[500_16],
		disabled: GREY[500_80],
		disabledBackground: GREY[500_24],
		focus: GREY[500_24],
		hoverOpacity: 0.08,
		disabledOpacity: 0.48,
	},
});

export default palette;
